import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, ErrorBox, Skeleton } from "../../components/ui";
import { useAgencyCorrespondence, useCorrespondenceMutation, type MailReadiness } from "../../lib/api";

const STATE_PILL: Record<string, string> = {
  Delivered: "bg-green-50 text-status-green",
  Queued: "bg-blue-50 text-brand-primary",
  "Recorded only": "bg-brand-grey-light text-brand-charcoal",
};

/** What has been sent to the recruitment agencies, and what came back (WP-23.1, OS-0619).
 *
 * The banner is not decoration. Frappe returns a dummy outgoing account while the site is muted, so a message
 * "sends" and goes nowhere — this deployment has 119 queue rows proving it. Every message here therefore carries
 * the state it is truthfully in, and only "Delivered" means an agency received anything. */
export default function Correspondence() {
  const { t } = useTranslation();
  const q = useAgencyCorrespondence();
  const send = useCorrespondenceMutation();
  const [open, setOpen] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-40" />)}</div>;
  const d = q.data;
  const chasing = d.agencies.flatMap((a) => a.chase.map((c) => ({ ...c, agency: a.name })));

  return (
    <div className="mt-4 space-y-6" data-rec-correspondence>
      <MailBanner readiness={d.readiness} />

      {chasing.length > 0 && (
        <Card className="p-4" data-rec-chase>
          <h2 className="font-heading text-base font-semibold">{t("rec.chase.title")}</h2>
          <p className="mt-1 text-[11px] text-brand-grey">{t("rec.chase.hint", { days: d.chase_after_days })}</p>
          <ul className="mt-2 space-y-1 text-sm">
            {chasing.map((c) => (
              <li key={c.reference_name} className="flex items-center justify-between gap-2 border-t border-brand-grey-light/70 py-1.5">
                <span className="min-w-0 truncate"><span className="font-medium">{c.job_title}</span>
                  <span className="ml-2 text-brand-grey">{c.agency}</span></span>
                <span className="shrink-0 rounded bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-status-amber">
                  {t("rec.chase.quiet", { days: c.days_quiet })}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {d.agencies.map((a) => (
        <Card key={a.name} className="overflow-hidden" data-rec-agency={a.name}>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-grey-light px-4 py-3">
            <div>
              <h2 className="font-heading text-base font-semibold">{a.name}</h2>
              <div className="text-[11px] text-brand-grey">{[a.contact_person, a.contact_email].filter(Boolean).join(" · ")}</div>
            </div>
            <span className="text-xs text-brand-grey">{t("rec.threadsCount", { n: a.threads.length })}</span>
          </div>

          {a.threads.length === 0 ? (
            <p className="p-6 text-center text-sm text-brand-grey">{t("rec.noCorrespondence")}</p>
          ) : (
            <ul className="divide-y divide-brand-grey-light">
              {a.threads.map((th) => (
                <li key={th.reference_name} className="px-4 py-3" data-rec-thread={th.reference_name}>
                  <button onClick={() => setOpen(open === th.reference_name ? null : th.reference_name)}
                    className="flex w-full items-center justify-between gap-2 text-left">
                    <span className="min-w-0">
                      <span className="text-sm font-semibold">{th.job_title ?? th.reference_name}</span>
                      <span className="ml-2 text-[11px] text-brand-grey">{th.company}</span>
                    </span>
                    <span className="shrink-0 text-[11px] text-brand-grey">
                      {t("rec.sentReceived", { sent: th.sent, received: th.received })}
                      {th.awaiting_reply && <span className="ml-2 text-status-amber">{t("rec.awaitingReply")}</span>}
                    </span>
                  </button>

                  {open === th.reference_name && (
                    <div className="mt-3 space-y-3">
                      {th.messages.map((m) => (
                        <div key={m.name} className={`rounded-lg border p-3 text-sm ${m.sent_or_received === "Sent" ? "border-brand-grey-light bg-white" : "border-brand-primary/30 bg-blue-50/40"}`}>
                          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-brand-grey">
                            <span>{m.sent_or_received === "Sent" ? t("rec.toAgency", { to: m.recipients }) : t("rec.fromAgency", { from: m.sender })}
                              {" · "}{(m.communication_date ?? "").slice(0, 16)}</span>
                            <span className={`rounded px-2 py-0.5 font-semibold ${STATE_PILL[m.state] ?? ""}`}>
                              {t(`rec.state.${m.state}`, m.state)}
                            </span>
                          </div>
                          <div className="mt-1 font-medium">{m.subject}</div>
                          <div className="prose prose-sm mt-1 max-w-none text-brand-charcoal"
                            dangerouslySetInnerHTML={{ __html: m.content ?? "" }} />
                        </div>
                      ))}

                      {d.is_hr && (
                        <div className="rounded-lg border border-brand-grey-light p-3">
                          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={4}
                            data-rec-reply-text placeholder={t("rec.followUpPlaceholder")}
                            className="w-full rounded border border-brand-grey-light px-2 py-1 text-sm" />
                          <div className="mt-2 flex items-center justify-end gap-2">
                            {send.isError && <ErrorBox error={send.error} />}
                            <button disabled={send.isPending || draft.trim().length < 10} data-rec-send-followup
                              onClick={() => send.mutate({ method: "reply_on_thread",
                                params: { job_posting: th.reference_name, content: draft } },
                                { onSuccess: () => setDraft("") })}
                              className="rounded bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
                              {send.isPending ? t("rec.sending") : t("rec.sendFollowUp")}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {a.postings.filter((p) => !a.threads.some((th) => th.reference_name === p.name)).map((p) => (
            <div key={p.name} className="flex items-center justify-between gap-2 border-t border-brand-grey-light px-4 py-2 text-sm">
              <span>{p.job_title ?? p.job_opening} <span className="text-[11px] text-brand-grey">{t("rec.notSentYet")}</span></span>
              {d.is_hr && (
                <button disabled={send.isPending} data-rec-send-posting
                  onClick={() => send.mutate({ method: "send_posting", params: { job_posting: p.name } })}
                  className="rounded bg-brand-primary px-2 py-1 text-xs font-semibold text-white disabled:opacity-50">
                  {t("rec.sendJd")}
                </button>
              )}
            </div>
          ))}
        </Card>
      ))}
    </div>
  );
}

function MailBanner({ readiness }: { readiness: MailReadiness }) {
  const { t } = useTranslation();
  if (readiness.can_send && readiness.can_receive) {
    return (
      <div className="rounded-lg border border-status-green/40 bg-green-50 px-4 py-2 text-sm text-status-green" data-rec-mail-banner>
        {t("rec.mail.ready", { account: readiness.outgoing_account })}
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-status-amber/40 bg-amber-50 px-4 py-3 text-sm" data-rec-mail-banner>
      <div className="font-semibold text-status-amber">{t("rec.mail.notReady")}</div>
      <ul className="mt-1 list-disc pl-5 text-[12px] text-brand-charcoal">
        {readiness.blockers.map((b) => <li key={b}>{b}</li>)}
      </ul>
      <p className="mt-1 text-[11px] text-brand-grey">{t("rec.mail.recordedAnyway")}</p>
    </div>
  );
}
